/**
 * InteractionHandler - Handles click detection and selection in the 3D scene
 */
import * as THREE from 'three';

export class InteractionHandler {
  constructor(renderer, camera) {
    this.renderer = renderer;
    this.camera = camera;
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    
    // Callbacks
    this.onWaypointClick = null;
    this.onViaPointClick = null;
    this.onFeatureClick = null;
    this.onDeselect = null;
    
    // Selectable mesh sources
    this.waypointMeshes = [];
    this.viaPointMeshes = [];
    this.featureMeshes = [];
    
    // Bind click handler
    this.handleClick = this.handleClick.bind(this);
    this.renderer.domElement.addEventListener('click', this.handleClick);
  }

  /**
   * Update the mesh sources for raycasting
   * @param {Object} sources - Object containing mesh arrays
   */
  updateMeshSources(sources) {
    if (sources.waypointMeshes) this.waypointMeshes = sources.waypointMeshes;
    if (sources.viaPointMeshes) this.viaPointMeshes = sources.viaPointMeshes;
    if (sources.featureMeshes) this.featureMeshes = sources.featureMeshes;
  }

  /**
   * Handle click events on the canvas
   * @param {MouseEvent} event - Click event
   */
  handleClick(event) {
    // Calculate mouse position in normalized device coordinates
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // Raycast to find intersections
    this.raycaster.setFromCamera(this.mouse, this.camera);
    
    // Check features first
    const featureHit = this.checkFeatureIntersection();
    if (featureHit !== null) return;
    
    // Check waypoints and via-points
    const waypointHit = this.checkWaypointIntersection();
    if (waypointHit !== null) return;
    
    // Nothing hit - deselect
    if (this.onDeselect) {
      this.onDeselect();
    }
  }

  /**
   * Check for feature intersection
   * @returns {number|null} Feature index or null
   */
  checkFeatureIntersection() {
    const intersects = this.raycaster.intersectObjects(this.featureMeshes, true);
    
    if (intersects.length > 0) {
      // Find the parent group with feature data
      let featureGroup = intersects[0].object;
      while (featureGroup.parent && !featureGroup.userData.feature) {
        featureGroup = featureGroup.parent;
      }

      if (featureGroup.userData.feature && this.onFeatureClick) {
        this.onFeatureClick(featureGroup.userData.index);
        return featureGroup.userData.index;
      }
    }
    
    return null;
  }

  /**
   * Check for waypoint/via-point intersection
   * @returns {Object|null} Hit info or null
   */
  checkWaypointIntersection() {
    const allSelectableGroups = [...this.waypointMeshes, ...this.viaPointMeshes];
    const intersects = this.raycaster.intersectObjects(allSelectableGroups, true);

    if (intersects.length > 0) {
      // Find the parent group
      let clickedGroup = intersects[0].object;
      while (clickedGroup.parent && !clickedGroup.userData.isWaypoint && !clickedGroup.userData.isViaPoint) {
        clickedGroup = clickedGroup.parent;
      }

      // Check if it's a via-point
      if (clickedGroup.userData.isViaPoint) {
        const waypointIndex = clickedGroup.userData.waypointIndex;
        if (this.onViaPointClick) {
          this.onViaPointClick(waypointIndex, clickedGroup.userData);
        }
        return { type: 'viaPoint', index: waypointIndex };
      } 
      
      // It's a regular waypoint
      if (clickedGroup.userData.isWaypoint) {
        const index = this.waypointMeshes.indexOf(clickedGroup);
        if (index !== -1 && this.onWaypointClick) {
          this.onWaypointClick(index);
        }
        return { type: 'waypoint', index };
      }
    }
    
    return null;
  }

  /**
   * Clean up event listeners
   */
  dispose() {
    this.renderer.domElement.removeEventListener('click', this.handleClick);
  }
}
